import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Form } from 'react-bootstrap';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { formatRoleLabel } from '../../utils/roleLabels';

const MODULE_ICONS = {
  sign_in: 'pi-sign-in',
  home: 'pi-home',
  baseline: 'pi-compass',
  people: 'pi-users',
  leave: 'pi-calendar',
  overtime: 'pi-clock',
  policies: 'pi-file',
  performance: 'pi-chart-bar',
  resignation: 'pi-user-minus',
  payroll: 'pi-wallet',
  projects: 'pi-briefcase',
  kanban: 'pi-th-large',
  planning: 'pi-calendar-plus',
  daily_tasks: 'pi-list-check',
  brand: 'pi-palette',
  clients: 'pi-id-card',
  finance: 'pi-dollar',
  tickets: 'pi-ticket',
  secret_santa: 'pi-gift',
  outside: 'pi-shield',
};

function errorMessage(error, fallback) {
  return error?.response?.data?.message || fallback;
}

function includesTerm(value, term) {
  return String(value || '').toLowerCase().includes(term);
}

function PermissionCard({ id, title, icon, open, onToggle, children }) {
  const innerRef = useRef(null);

  useEffect(() => {
    if (innerRef.current) innerRef.current.inert = !open;
  }, [open]);

  return (
    <section className={`perm-card ${open ? 'is-open' : ''}`} id={`perm-section-${id}`}>
      <button
        type="button"
        className="perm-card-header"
        aria-expanded={open}
        aria-controls={`perm-panel-${id}`}
        onClick={onToggle}
      >
        <span className="perm-card-icon" aria-hidden="true">
          <i className={`pi ${icon || 'pi-lock'}`} />
        </span>
        <span className="perm-card-title">{title}</span>
        <i className={`pi ${open ? 'pi-chevron-up' : 'pi-chevron-down'} perm-card-chevron`} aria-hidden="true" />
      </button>
      <div className={`perm-card-panel ${open ? 'is-open' : ''}`} id={`perm-panel-${id}`}>
        <div className="perm-card-panel-inner" ref={innerRef} aria-hidden={!open}>
          <div className="perm-card-body">{children}</div>
        </div>
      </div>
    </section>
  );
}

const RoleEditor = ({
  request,
  editor,
  roles,
  catalog,
  canManage,
  actorPermissions,
  initialKeys,
  onClose,
  onSaved,
  notify,
}) => {
  const isCreate = editor.mode === 'create';
  const readOnly = editor.mode === 'view' || !canManage || Boolean(editor.role?.isSystem);
  const [roleName, setRoleName] = useState(editor.role?.roleName || '');
  const [description, setDescription] = useState(editor.role?.description || '');
  const [granted, setGranted] = useState(initialKeys || []);
  const [draft, setDraft] = useState(initialKeys || []);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [openIds, setOpenIds] = useState({});
  const [collapsedWhileSearching, setCollapsedWhileSearching] = useState({});

  useEffect(() => {
    if (isCreate || initialKeys) return;
    request(`/roles/${encodeURIComponent(editor.role.roleName)}/permissions`)
      .then((data) => {
        setGranted(data.permissionKeys || []);
        setDraft(data.permissionKeys || []);
      })
      .catch((err) => notify('error', errorMessage(err, 'Could not load role permissions')));
  }, [isCreate, initialKeys, editor, request, notify]);

  const grantChanges = useMemo(() => {
    const grants = {};
    const before = new Set(granted);
    const after = new Set(draft);
    before.forEach((key) => { if (!after.has(key)) grants[key] = false; });
    after.forEach((key) => { if (!before.has(key)) grants[key] = true; });
    return grants;
  }, [granted, draft]);

  const toggleKey = (key) => {
    setDraft((current) => (
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    ));
  };

  const permissionCheck = (key, label) => {
    const checked = draft.includes(key);
    const cannotGrant = !checked && !actorPermissions.includes(key);
    return (
      <Form.Check
        className="perm-check"
        type="checkbox"
        checked={checked}
        disabled={readOnly || cannotGrant}
        onChange={() => toggleKey(key)}
        aria-label={label || key}
        title={cannotGrant ? 'You cannot grant a permission you do not have' : key}
      />
    );
  };

  const rowEntries = (module, row) => (module.actions || [])
    .map((action) => ({ action, key: row.cells?.[action.id] }))
    .filter((entry) => entry.key);

  const term = search.trim().toLowerCase();

  useEffect(() => {
    setCollapsedWhileSearching({});
  }, [term]);

  const firstId = catalog.modules[0]?.id;
  const isOpen = (id) => (
    term ? !collapsedWhileSearching[id] : Boolean(openIds[id] ?? id === firstId)
  );
  const toggleSection = (id) => {
    if (term) {
      setCollapsedWhileSearching((current) => ({ ...current, [id]: !current[id] }));
      return;
    }
    setOpenIds((current) => ({ ...current, [id]: !(current[id] ?? id === firstId) }));
  };

  const visibleModules = useMemo(() => {
    return catalog.modules.map((module) => {
      if (!term) return module;
      const titleHit = includesTerm(module.title, term)
        || includesTerm(module.note, term)
        || includesTerm(module.description, term);
      if (module.baseline) {
        const keys = titleHit
          ? module.keys
          : (module.keys || []).filter((item) => (
            includesTerm(item.label, term)
            || includesTerm(item.description, term)
            || includesTerm(item.key, term)
          ));
        return keys.length ? { ...module, keys } : null;
      }
      const rows = (module.rows || []).filter((row) => {
        const keyed = (module.actions || []).some((action) => row.cells?.[action.id]);
        if (!keyed) return false;
        if (!term || titleHit || includesTerm(row.label, term) || includesTerm(row.description, term)) return true;
        return (module.actions || []).some((action) => {
          const key = row.cells?.[action.id];
          return key && (
            includesTerm(action.label, term)
            || includesTerm(row.actionHelp?.[action.id], term)
            || includesTerm(key, term)
          );
        });
      });
      return rows.length ? { ...module, rows } : null;
    }).filter(Boolean);
  }, [catalog.modules, term]);

  const visibleOutside = useMemo(() => {
    const items = catalog.outsideInventory || [];
    if (!term) return items;
    const titleHit = includesTerm('Outside the product inventory', term);
    return items.filter((item) => (
      titleHit
      || includesTerm(item.label, term)
      || includesTerm(item.description, term)
      || includesTerm(item.key, term)
    ));
  }, [catalog.outsideInventory, term]);

  const hasResults = visibleModules.length > 0 || visibleOutside.length > 0;

  const renderBlocks = (module, rows) => {
    const blocks = [];
    let singles = [];
    const flushSingles = () => {
      if (!singles.length) return;
      blocks.push({ type: 'singles', rows: singles });
      singles = [];
    };
    rows.forEach((row) => {
      const entries = rowEntries(module, row);
      if (!entries.length) return;
      if (entries.length === 1) {
        singles.push({ row, entry: entries[0] });
        return;
      }
      flushSingles();
      blocks.push({ type: 'multi', row, entries });
    });
    flushSingles();
    return blocks;
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isCreate) {
        await request('/roles', { method: 'post', data: { roleName, description } });
      } else if (description !== (editor.role.description || '')) {
        await request(`/roles/${encodeURIComponent(roleName)}`, { method: 'put', data: { description } });
      }
      if (Object.keys(grantChanges).length) {
        await request(`/roles/${encodeURIComponent(roleName)}/permissions`, {
          method: 'put',
          data: { grants: grantChanges },
        });
      }
      const label = formatRoleLabel(roleName);
      await onSaved(isCreate ? `${label} created.` : `${label} saved.`);
    } catch (err) {
      notify('error', errorMessage(err, 'Could not save the role'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await request(`/roles/${encodeURIComponent(roleName)}`, { method: 'delete' });
      await onSaved(`${formatRoleLabel(roleName)} deleted.`);
    } catch (err) {
      notify('error', errorMessage(err, 'Could not delete the role'));
    }
  };

  const descriptionChanged = !isCreate && description !== (editor.role?.description || '');
  const canSave = !readOnly && (
    isCreate
      ? Boolean(String(roleName || '').trim())
      : descriptionChanged || Object.keys(grantChanges).length > 0
  );
  const title = isCreate ? 'Add role' : (readOnly ? 'View role' : 'Edit role');

  return (
    <div className="iam-permissions">
      <button type="button" className="perm-back" onClick={onClose}>
        <i className="pi pi-angle-left" aria-hidden="true" />
        Back to roles
      </button>

      <div className="iam-permissions-header">
        <div className="iam-permissions-heading">
          <h2 className="iam-permissions-title">
            Permissions
            {editor.role?.isSystem && <Badge bg="secondary" className="perm-system-badge">System</Badge>}
          </h2>
          <p className="iam-permissions-subtitle">Manage what employees can access and do in the system.</p>
        </div>
        <div className="iam-perm-search">
          <i className="pi pi-search iam-perm-search-icon" aria-hidden="true" />
          <InputText
            value={search}
            placeholder="Search permissions..."
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search permissions"
          />
        </div>
      </div>

      <section className="perm-card perm-role-card">
        <div className="perm-role-card-head">{title}</div>
        <div className="perm-role-fields">
          <Form.Group>
            <Form.Label>Role name</Form.Label>
            {isCreate ? (
              <>
                <Form.Control
                  value={roleName}
                  placeholder="e.g. Quality Manager"
                  maxLength={100}
                  onChange={(event) => setRoleName(event.target.value)}
                  aria-label="New role name"
                />
                <p className="perm-note mt-2 mb-0">
                  Use a clear name (letters, numbers, spaces, hyphens, or underscores). You can set permissions below before saving.
                </p>
              </>
            ) : (
              <Form.Control value={formatRoleLabel(roleName)} disabled />
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              disabled={readOnly}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Form.Group>
        </div>
      </section>

      {visibleModules.map((module) => (
        <PermissionCard
          key={module.id}
          id={module.id}
          title={module.title}
          icon={MODULE_ICONS[module.id]}
          open={isOpen(module.id)}
          onToggle={() => toggleSection(module.id)}
        >
          {module.baseline ? (
            <>
              {module.description && <p className="perm-note">{module.description}</p>}
              {module.note && <p className="perm-note">{module.note}</p>}
              <div className="perm-stack perm-stack-muted">
                {(module.keys || []).map((item) => (
                  <div key={item.key} className="perm-option perm-option-static">
                    <span className="perm-option-copy">
                      <span className="perm-option-label">
                        {item.label}
                        {draft.includes(item.key) ? '' : ' — not granted to this role'}
                      </span>
                      {item.description && <span className="perm-option-desc">{item.description}</span>}
                    </span>
                  </div>
                ))}
              </div>
              <p className="perm-note perm-note-foot">Check in/out and raising a ticket have no separate permission.</p>
            </>
          ) : (
            <>
              {module.description && <p className="perm-note">{module.description}</p>}
              {renderBlocks(module, module.rows || []).map((block) => (
                block.type === 'multi' ? (
                  <div key={block.row.label} className="perm-group">
                    <div className="perm-subhead">{block.row.label}</div>
                    {block.row.description && <p className="perm-note">{block.row.description}</p>}
                    <div className="perm-inline">
                      {block.entries.map(({ action, key }) => (
                        <label key={key} className="perm-option">
                          {permissionCheck(key, `${block.row.label}: ${action.label}`)}
                          <span className="perm-option-copy">
                            <span className="perm-option-label">{action.label}</span>
                            {block.row.actionHelp?.[action.id] && (
                              <span className="perm-option-desc">{block.row.actionHelp[action.id]}</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    key={block.rows.map(({ row }) => row.label).join('|')}
                    className={`perm-stack ${block.rows.length > 1 ? 'perm-stack-muted' : ''}`}
                  >
                    {block.rows.map(({ row, entry }) => (
                      <label key={row.label} className="perm-option">
                        {permissionCheck(entry.key, row.label)}
                        <span className="perm-option-copy">
                          <span className="perm-option-label">{row.label}</span>
                          {row.description && <span className="perm-option-desc">{row.description}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                )
              ))}
            </>
          )}
        </PermissionCard>
      ))}

      {visibleOutside.length > 0 && (
        <PermissionCard
          id="outside"
          title="Outside the product inventory"
          icon={MODULE_ICONS.outside}
          open={isOpen('outside')}
          onToggle={() => toggleSection('outside')}
        >
          <div className={`perm-stack ${visibleOutside.length > 1 ? 'perm-stack-muted' : ''}`}>
            {visibleOutside.map((item) => (
              <label key={item.key} className="perm-option">
                {permissionCheck(item.key, item.label)}
                <span className="perm-option-copy">
                  <span className="perm-option-label">{item.label}</span>
                  {item.description && <span className="perm-option-desc">{item.description}</span>}
                </span>
              </label>
            ))}
          </div>
        </PermissionCard>
      )}

      {!hasResults && <p className="perm-empty">No permissions found.</p>}

      <div className="perm-save-row">
        {!readOnly && (
          <Button
            label="Save"
            severity="help"
            loading={saving}
            disabled={!canSave || saving}
            onClick={save}
          />
        )}
        {!readOnly && !isCreate && (
          <Button
            label="Delete"
            severity="danger"
            outlined
            disabled={editor.role.userCount > 0}
            tooltip={editor.role.userCount > 0 ? 'This role still has users' : undefined}
            onClick={remove}
          />
        )}
        <Button label={readOnly ? 'Close' : 'Cancel'} outlined severity="secondary" onClick={onClose} />
      </div>
    </div>
  );
};

export default RoleEditor;
